import { gql } from '@apollo/client';
import { Heading } from '@apollo/orbit';
import React from 'react';

import {
  LaunchesListFragment,
  LaunchesListInfo,
  computeLaunchesListInfo,
} from 'src/app/graph/launchesPage/LaunchesList/helpers/computeLaunchesListInfo';
import { LaunchesList } from 'src/app/graph/launchesPage/LaunchesList/LaunchesList';
import { LaunchesListSummaryItem } from 'src/app/graph/launchesPage/LaunchesList/LaunchesListItem/LaunchesListSummaryItem';
import { CollapsibleDrawer } from 'src/components/collapsibleDrawer/CollapsibleDrawer';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { launchStatusForStep } from './helpers';
import { LaunchSequenceStepHeader } from './LaunchSequenceStepHeader';
import { LaunchStepProgressBar } from './launchStepProgressBar/LaunchStepProgressBar';

export const LaunchSequenceStepFragment = gql`
  fragment LaunchSequenceStepFragment on LaunchSequenceStep {
    ... on LaunchSequenceInitiatedStep {
      startedAt
    }
    ... on LaunchSequenceBuildStep {
      startedAt
      completedAt
    }
    ... on LaunchSequencePublishStep {
      startedAt
      completedAt
    }
    ... on LaunchSequenceSupersededStep {
      completedAt
    }
    ... on LaunchSequenceCompletedStep {
      completedAt
    }
    __typename
  }
`;

export const LaunchSequenceLaunchFragment = gql`
  fragment LaunchSequenceLaunchFragment on Launch {
    id
    createdAt
    status
    build {
      result {
        __typename
      }
    }
    buildInput {
      __typename
    }
    schemaTag {
      id
      diffToPrevious {
        severity
      }
    }
    sequence {
      ...LaunchSequenceStepFragment
    }
    supersededBy {
      id
    }
    ...LaunchesListFragment
  }
  ${LaunchSequenceStepFragment}
  ${LaunchesListFragment}
`;

type LaunchSequenceStepProps = {
  step: GraphQLTypes.LaunchSequenceStepFragment;
  launch: GraphQLTypes.LaunchSequenceLaunchFragment;
};

const LaunchSequenceStep = ({ launch, step }: LaunchSequenceStepProps) => {
  const buildLaunchesListInfo = computeLaunchesListInfo({
    parentLaunch: launch,
    kind: 'build',
  });
  const completedLaunchesListInfo = computeLaunchesListInfo({
    parentLaunch: launch,
    kind: 'launch',
  });
  const supersedingLaunchListInfo = computeLaunchesListInfo({
    parentLaunch: launch,
    kind: 'supersede',
  });

  const launchesListInfo: LaunchesListInfo =
    step.__typename === 'LaunchSequenceBuildStep'
      ? buildLaunchesListInfo
      : step.__typename === 'LaunchSequenceCompletedStep' &&
          supersedingLaunchListInfo.launches.length > 0
        ? supersedingLaunchListInfo
        : completedLaunchesListInfo;

  launchesListInfo.launches = launchesListInfo.launches.filter(
    ({ isStarted }) => isStarted,
  );

  const shouldUseDrawer =
    (step.__typename === 'LaunchSequenceBuildStep' ||
      step.__typename === 'LaunchSequenceCompletedStep') &&
    launchesListInfo.launches.length > 0;

  const stepStatus = launchStatusForStep(step, launch);

  return (
    <div className="mb-0.5 flex overflow-hidden">
      <LaunchStepProgressBar step={step} stepStatus={stepStatus} />

      {/* Step Header & Basic Info */}
      {shouldUseDrawer ? (
        <CollapsibleDrawer
          autoCollapse
          buttonPosition="top-left"
          className="mb-2 mr-1.5 w-full"
          header={
            <div className="mr-3">
              <LaunchSequenceStepHeader launch={launch} step={step} />
            </div>
          }
          content={
            <LaunchesList
              className="mt-2"
              launchesInfo={launchesListInfo}
              linkLaunches
            />
          }
          closedPlaceholder={
            <LaunchesListSummaryItem
              className="mb-1 mt-2"
              launchesInfo={launchesListInfo}
            />
          }
        />
      ) : (
        <LaunchSequenceStepHeader launch={launch} step={step} />
      )}
    </div>
  );
};

type LaunchSequenceProps = {
  launch: GraphQLTypes.LaunchSequenceLaunchFragment;
};

export const LaunchSequence = ({ launch }: LaunchSequenceProps) => {
  return (
    <>
      <Heading as="h3" className="mb-4 truncate">
        Launch sequence
      </Heading>
      {launch.sequence
        .slice()
        .reverse()
        .map((step) => (
          <LaunchSequenceStep
            key={step.__typename}
            launch={launch}
            step={step}
          />
        ))}
    </>
  );
};
